import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Criteria, getDefaultCriteria } from "@/types/criteria";
import { PerformanceThreshold, getDefaultThresholds } from "@/types/performanceThresholds";
import EvaluationSettings from "@/components/EvaluationSettings";
import UserManagement from "@/components/UserManagement";
import DepartmentSettings from "@/components/DepartmentSettings";
import CompanySettings from "@/components/CompanySettings";
import { CostCategoriesSettings } from "@/components/CostCategoriesSettings";
import { ToolDefinitionsSettings } from "@/components/ToolDefinitionsSettings";
import { ImportSettings } from "@/components/ImportSettings";
import { NotificationSettings } from "@/components/NotificationSettings";
import { RecruitingSettings } from "@/components/contracts/RecruitingSettings";
import { DocumentTemplatesSettings } from "@/components/contracts/DocumentTemplatesSettings";
import { VehicleSettings } from "@/components/VehicleSettings";
import { CostCalculationSettings } from "@/components/CostCalculationSettings";
import VersionManagement from "@/components/VersionManagement";
import MobileDeviceSettings from "@/components/MobileDeviceSettings";
import SimCardSettings from "@/components/SimCardSettings";
import WorkProcessesSettings from "@/components/WorkProcessesSettings";
import { AuditLogSettings } from "@/components/AuditLogSettings";
import { ConversationFieldSettings } from "@/components/ConversationFieldSettings";
import SurveySettings from "@/components/SurveySettings";
import { PasskeyManagement } from "@/components/auth/PasskeyManagement";
import SafetyInstructionsSettings from "@/components/safety/SafetyInstructionsSettings";

import { useAuth } from "@/hooks/useAuth";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import {
  Menu,
  Users,
  Bell,
  Upload,
  Building2,
  Network,
  Car,
  Wallet,
  Wrench,
  Calculator,
  ClipboardCheck,
  ClipboardList,
  History as HistoryIcon,
  UserPlus,
  FileText,
  Smartphone,
  CreditCard,
  GitBranch,
  Eye,
  MessageSquare,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ArrowLeft,
  Globe,
  Shield,
  ShieldAlert,
  Settings as SettingsIcon,
  Package,
  Sun,
  Moon,
  type LucideIcon,
} from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useTheme } from "@/hooks/useTheme";

interface SettingsTab {
  value: string;
  label: string;
  icon: LucideIcon;
}

interface SettingsGroup {
  key: string;
  label: string;
  icon: LucideIcon;
  tabs: SettingsTab[];
}

const settingsGroups: SettingsGroup[] = [
  {
    key: "company",
    label: "Unternehmen",
    icon: Building2,
    tabs: [
      { value: "company", label: "Firma", icon: Building2 },
      { value: "departments", label: "Abteilungen", icon: Network },
      { value: "workprocesses", label: "Arbeitsprozesse", icon: GitBranch },
      { value: "survey", label: "Mitarbeiterumfragen", icon: ClipboardList },
    ],
  },
  {
    key: "operations",
    label: "Betriebsausstattung",
    icon: Package,
    tabs: [
      { value: "tools", label: "Werkzeuge", icon: Wrench },
      { value: "mobiledevices", label: "Firmenhandys", icon: Smartphone },
      { value: "simcards", label: "SIM-Karten", icon: CreditCard },
      { value: "vehicles", label: "Fahrzeuge", icon: Car },
    ],
  },
  {
    key: "system",
    label: "Systemeinstellungen",
    icon: SettingsIcon,
    tabs: [
      { value: "users", label: "Benutzerverwaltung", icon: Users },
      { value: "security", label: "Sicherheit", icon: Shield },
      { value: "notifications", label: "Benachrichtigungen", icon: Bell },
      { value: "import", label: "Import", icon: Upload },
      { value: "versions", label: "Versionen", icon: HistoryIcon },
      { value: "auditlog", label: "Audit-Log", icon: Eye },
    ],
  },
  {
    key: "hr",
    label: "Personal & Dokumente",
    icon: UserPlus,
    tabs: [
      { value: "recruiting", label: "Recruiting", icon: UserPlus },
      { value: "documenttemplates", label: "Dokumentvorlagen", icon: FileText },
      { value: "safetyinstructions", label: "Sicherheitsunterweisungen", icon: ShieldAlert },
      { value: "conversations", label: "Mitarbeitergespräche", icon: MessageSquare },
      { value: "evaluation", label: "Bewertung", icon: ClipboardCheck },
    ],
  },
  {
    key: "costs",
    label: "Kosten",
    icon: Calculator,
    tabs: [
      { value: "costcategories", label: "Kostenarten", icon: Wallet },
      { value: "costcalculation", label: "Einstandslohn", icon: Calculator },
    ],
  },
];

const allTabs: SettingsTab[] = settingsGroups.flatMap((g) => g.tabs);

const SettingsPage = () => {
  const { user, userRole } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { isDarkMode, toggleTheme } = useTheme();
  const [activeTab, setActiveTab] = useState("company");
  const [sheetOpen, setSheetOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    return localStorage.getItem("settings-sidebar-collapsed") === "true";
  });
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    try {
      const stored = localStorage.getItem("settings-open-groups");
      if (stored) return JSON.parse(stored);
    } catch {}
    return Object.fromEntries(settingsGroups.map((g) => [g.key, true]));
  });

  const isAdmin = userRole === "admin" || userRole === "hr_manager";
  const isHrClerk = userRole === "hr_clerk";

  const hrClerkAllowedTabs = [
    "security",
    "mobiledevices",
    "simcards",
    "vehicles",
    "import",
    "documenttemplates",
  ];

  const [criteria, setCriteria] = useState<Criteria[]>(() => {
    const stored = localStorage.getItem("criteria");
    return stored ? JSON.parse(stored) : getDefaultCriteria();
  });

  const [thresholds, setThresholds] = useState<PerformanceThreshold[]>(() => {
    const stored = localStorage.getItem("performanceThresholds");
    return stored ? JSON.parse(stored) : getDefaultThresholds();
  });

  useEffect(() => {
    localStorage.setItem("settings-sidebar-collapsed", isSidebarCollapsed.toString());
  }, [isSidebarCollapsed]);

  useEffect(() => {
    localStorage.setItem("settings-open-groups", JSON.stringify(openGroups));
  }, [openGroups]);

  // Filter groups for hr_clerk
  const visibleGroups = useMemo<SettingsGroup[]>(() => {
    if (!isHrClerk) return settingsGroups;
    return settingsGroups
      .map((g) => ({
        ...g,
        tabs: g.tabs.filter((t) => hrClerkAllowedTabs.includes(t.value)),
      }))
      .filter((g) => g.tabs.length > 0);
  }, [isHrClerk]);

  const visibleTabs = useMemo(
    () => visibleGroups.flatMap((g) => g.tabs),
    [visibleGroups]
  );

  // Ensure activeTab is allowed
  useEffect(() => {
    if (!visibleTabs.find((t) => t.value === activeTab) && visibleTabs[0]) {
      setActiveTab(visibleTabs[0].value);
    }
  }, [visibleTabs, activeTab]);

  useEffect(() => {
    const loadCriteria = async () => {
      const defaults = getDefaultCriteria();
      try {
        const { data, error } = await supabase
          .from("evaluation_criteria_settings")
          .select("criteria")
          .eq("id", "global")
          .maybeSingle();

        if (!error && data?.criteria && Array.isArray(data.criteria) && data.criteria.length > 0) {
          const dbCriteria = (data.criteria as unknown as Criteria[]).map((c) => {
            const defaultCriterion = defaults.find((d) => d.key === c.key);
            return {
              ...c,
              criteriaType: c.criteriaType || defaultCriterion?.criteriaType || "standard",
              weight: c.weight || defaultCriterion?.weight || 1,
              baselinePoints: c.baselinePoints ?? c.defaultPoints,
              minPoints: c.minPoints ?? 0,
              maxPoints: c.maxPoints ?? c.defaultPoints,
            };
          });
          setCriteria(dbCriteria);
          localStorage.setItem("criteria", JSON.stringify(dbCriteria));
          return;
        }
      } catch (e) {
        console.error("Error loading criteria from database:", e);
      }

      const stored = localStorage.getItem("criteria");
      if (stored) {
        try {
          const existingCriteria = JSON.parse(stored) as Criteria[];
          const migratedCriteria = existingCriteria.map((c) => {
            const defaultCriterion = defaults.find((d) => d.key === c.key);
            return {
              ...c,
              description: c.description !== undefined ? c.description : defaultCriterion?.description || "",
              criteriaType: c.criteriaType || defaultCriterion?.criteriaType || "standard",
              weight: c.weight || defaultCriterion?.weight || 1,
              baselinePoints: c.baselinePoints ?? c.defaultPoints,
              minPoints: c.minPoints ?? 0,
              maxPoints: c.maxPoints ?? c.defaultPoints,
            };
          });
          setCriteria(migratedCriteria);
        } catch (e) {
          console.error("Error migrating criteria:", e);
          setCriteria(defaults);
        }
      } else {
        localStorage.setItem("criteria", JSON.stringify(defaults));
        setCriteria(defaults);
      }
    };

    loadCriteria();
  }, [user]);

  const handleSaveCriteria = async (newCriteria: Criteria[]) => {
    localStorage.setItem("criteria", JSON.stringify(newCriteria));
    setCriteria(newCriteria);

    try {
      const { data: existing } = await supabase
        .from("evaluation_criteria_settings")
        .select("id")
        .eq("id", "global")
        .maybeSingle();

      if (existing) {
        await supabase
          .from("evaluation_criteria_settings")
          .update({
            criteria: JSON.parse(JSON.stringify(newCriteria)),
            updated_at: new Date().toISOString(),
            updated_by: user?.id || null,
          })
          .eq("id", "global");
      } else {
        await supabase.from("evaluation_criteria_settings").insert({
          id: "global",
          criteria: JSON.parse(JSON.stringify(newCriteria)),
          updated_at: new Date().toISOString(),
          updated_by: user?.id || null,
        } as any);
      }
    } catch (e) {
      console.error("Error saving criteria to database:", e);
    }

    window.dispatchEvent(new Event("storage-sync"));
  };

  const handleSaveThresholds = (newThresholds: PerformanceThreshold[]) => {
    localStorage.setItem("performanceThresholds", JSON.stringify(newThresholds));
    setThresholds(newThresholds);
    window.dispatchEvent(new Event("storage-sync"));
  };

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    setSheetOpen(false);
  };

  const toggleGroup = (key: string) => {
    setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const currentTab = visibleTabs.find((t) => t.value === activeTab);
  const currentGroup = visibleGroups.find((g) => g.tabs.some((t) => t.value === activeTab));

  const renderTabContent = () => {
    switch (activeTab) {
      case "users":
        return <UserManagement />;
      case "security":
        return <PasskeyManagement />;
      case "notifications":
        return <NotificationSettings />;
      case "import":
        return <ImportSettings />;
      case "company":
        return <CompanySettings />;
      case "departments":
        return <DepartmentSettings />;
      case "workprocesses":
        return <WorkProcessesSettings />;
      case "survey":
        return <SurveySettings />;
      case "recruiting":
        return <RecruitingSettings />;
      case "documenttemplates":
        return <DocumentTemplatesSettings />;
      case "safetyinstructions":
        return <SafetyInstructionsSettings />;
      case "vehicles":
        return <VehicleSettings />;
      case "mobiledevices":
        return <MobileDeviceSettings />;
      case "simcards":
        return <SimCardSettings />;
      case "costcategories":
        return <CostCategoriesSettings />;
      case "tools":
        return <ToolDefinitionsSettings />;
      case "costcalculation":
        return <CostCalculationSettings />;
      case "evaluation":
        return (
          <EvaluationSettings
            criteria={criteria}
            thresholds={thresholds}
            onSaveCriteria={handleSaveCriteria}
            onSaveThresholds={handleSaveThresholds}
          />
        );
      case "versions":
        return <VersionManagement />;
      case "auditlog":
        return <AuditLogSettings />;
      case "conversations":
        return <ConversationFieldSettings />;
      default:
        return <CompanySettings />;
    }
  };

  // Sidebar group + items renderer (desktop)
  const SidebarGroupBlock = ({ group }: { group: SettingsGroup }) => {
    const GroupIcon = group.icon;
    const isOpen = openGroups[group.key] ?? true;
    const containsActive = group.tabs.some((t) => t.value === activeTab);

    if (isSidebarCollapsed) {
      // collapsed: show only tab icons (skip group label) to keep mini nav usable
      return (
        <div className="flex flex-col gap-1 border-b border-border/50 pb-2 mb-1">
          {group.tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.value;
            return (
              <Tooltip key={tab.value}>
                <TooltipTrigger asChild>
                  <Button
                    variant={isActive ? "secondary" : "ghost"}
                    className={cn(
                      "w-full justify-center px-2 h-10",
                      isActive && "bg-primary text-primary-foreground hover:bg-primary/90"
                    )}
                    onClick={() => handleTabChange(tab.value)}
                  >
                    <Icon className="h-5 w-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right" className="font-medium">
                  <span className="text-xs text-muted-foreground mr-1">{group.label} ·</span>
                  {tab.label}
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      );
    }

    return (
      <div className="mb-2">
        <button
          type="button"
          onClick={() => toggleGroup(group.key)}
          className={cn(
            "flex items-center justify-between w-full px-2 py-2 rounded-md text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors",
            containsActive && "text-foreground"
          )}
        >
          <span className="flex items-center gap-2">
            <GroupIcon className="h-4 w-4" />
            {group.label}
          </span>
          <ChevronDown
            className={cn("h-4 w-4 transition-transform", !isOpen && "-rotate-90")}
          />
        </button>
        {isOpen && (
          <div className="flex flex-col gap-1 mt-1 ml-2 pl-2 border-l border-border">
            {group.tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.value;
              return (
                <Button
                  key={tab.value}
                  variant={isActive ? "secondary" : "ghost"}
                  className={cn(
                    "w-full justify-start gap-3 h-9 text-sm",
                    isActive && "bg-primary text-primary-foreground hover:bg-primary/90"
                  )}
                  onClick={() => handleTabChange(tab.value)}
                >
                  <Icon className="h-4 w-4 flex-shrink-0" />
                  <span className="font-medium truncate">{tab.label}</span>
                </Button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Mobile group renderer
  const MobileGroupBlock = ({ group }: { group: SettingsGroup }) => {
    const GroupIcon = group.icon;
    const isOpen = openGroups[group.key] ?? true;
    return (
      <div className="mb-2">
        <button
          type="button"
          onClick={() => toggleGroup(group.key)}
          className="flex items-center justify-between w-full px-3 py-2 rounded-md text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:bg-muted/50"
        >
          <span className="flex items-center gap-2">
            <GroupIcon className="h-4 w-4" />
            {group.label}
          </span>
          <ChevronDown
            className={cn("h-4 w-4 transition-transform", !isOpen && "-rotate-90")}
          />
        </button>
        {isOpen && (
          <div className="flex flex-col gap-1 mt-1 ml-2 pl-2 border-l border-border">
            {group.tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.value;
              return (
                <Button
                  key={tab.value}
                  variant={isActive ? "secondary" : "ghost"}
                  className={cn(
                    "w-full justify-start gap-3 h-11",
                    isActive && "bg-primary text-primary-foreground"
                  )}
                  onClick={() => handleTabChange(tab.value)}
                >
                  <Icon className="h-5 w-5 flex-shrink-0" />
                  <span className="font-medium">{tab.label}</span>
                </Button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const DesktopSidebar = () => (
    <aside
      className={cn(
        "hidden md:flex flex-col bg-card border-r border-border h-screen fixed left-0 top-0 transition-all duration-300 z-50",
        isSidebarCollapsed ? "w-16" : "w-64"
      )}
    >
      <div
        className={cn(
          "flex items-center border-b border-border p-3 gap-2",
          isSidebarCollapsed ? "justify-center flex-col" : "justify-between"
        )}
      >
        {!isSidebarCollapsed ? (
          <>
            <div className="flex items-center gap-2">
              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary text-primary-foreground flex-shrink-0">
                <Globe className="h-5 w-5" />
              </div>
              <span className="font-bold text-foreground">Flash HR</span>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              className="h-8 w-8"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
          </>
        ) : (
          <>
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary text-primary-foreground flex-shrink-0">
              <Globe className="h-5 w-5" />
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              className="h-8 w-8"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </>
        )}
      </div>

      <div className={cn("border-b border-border p-2 flex gap-2", isSidebarCollapsed ? "flex-col items-center" : "items-center")}>
        {isSidebarCollapsed ? (
          <>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="icon" onClick={() => navigate("/")} className="h-10 w-10">
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">Zurück</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="icon" onClick={toggleTheme} className="h-10 w-10">
                  {isDarkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">{isDarkMode ? "Light Mode" : "Dark Mode"}</TooltipContent>
            </Tooltip>
          </>
        ) : (
          <>
            <Button variant="outline" onClick={() => navigate("/")} className="flex-1 justify-start gap-2">
              <ArrowLeft className="h-4 w-4" />
              Zurück zum Menü
            </Button>
            <Button variant="outline" size="icon" onClick={toggleTheme} className="h-9 w-9 flex-shrink-0" title={isDarkMode ? "Light Mode" : "Dark Mode"}>
              {isDarkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
          </>
        )}
      </div>


      <div className={cn("border-b border-border p-3", isSidebarCollapsed ? "text-center" : "")}>
        {!isSidebarCollapsed && <h3 className="font-semibold text-foreground">Einstellungen</h3>}
      </div>

      <div className="flex-1 overflow-y-auto py-2 px-2">
        <nav className="flex flex-col">
          {visibleGroups.map((group) => (
            <SidebarGroupBlock key={group.key} group={group} />
          ))}
        </nav>
      </div>
    </aside>
  );

  return (
    <div className="flex min-h-screen">
      <DesktopSidebar />

      <div
        className={cn(
          "flex-1 flex flex-col min-w-0 transition-all duration-300",
          !isMobile && (isSidebarCollapsed ? "md:ml-16" : "md:ml-64")
        )}
      >
        {/* Mobile Header */}
        <div className="md:hidden sticky top-0 z-10 bg-background border-b border-border p-3">
          <div className="flex items-center gap-2 mb-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/")}
              className="h-10 w-10"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2">
              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary text-primary-foreground flex-shrink-0">
                <Globe className="h-5 w-5" />
              </div>
              <span className="font-bold text-foreground">Flash HR</span>
            </div>
          </div>
          <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" className="w-full justify-between h-12 bg-card">
                <span className="flex items-center gap-3">
                  <Menu className="h-5 w-5" />
                  {currentTab && (
                    <>
                      <currentTab.icon className="h-5 w-5 text-primary" />
                      <span className="font-medium">{currentTab.label}</span>
                    </>
                  )}
                </span>
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[320px] max-h-[100vh] overflow-y-auto bg-card p-0">
              <SheetHeader className="border-b border-border p-4">
                <SheetTitle className="flex items-center gap-2">
                  <Menu className="h-5 w-5" />
                  Einstellungen
                </SheetTitle>
              </SheetHeader>
              <div className="flex flex-col p-3 max-h-[calc(100vh-100px)] overflow-y-auto">
                {visibleGroups.map((group) => (
                  <MobileGroupBlock key={group.key} group={group} />
                ))}
              </div>
            </SheetContent>
          </Sheet>
        </div>

        {/* Content Area */}
        <div className="flex-1 p-4 md:p-6 overflow-auto">
          <div className="hidden md:block mb-6">
            {currentGroup && (
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                {currentGroup.label}
              </p>
            )}
            <h2 className="text-2xl font-bold text-foreground">{currentTab?.label}</h2>
          </div>

          <div className="animate-fade-in">{renderTabContent()}</div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
